import { useMemo, useState, type FormEvent } from 'react';
import { Surface, ghostLinkStyle, linkRowStyle, inputStyle, primaryButtonStyle } from '@kilat/ui';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { useAuth } from '../lib/use-auth.js';
import { useI18n, type I18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { campaignsRepoFor } from '../lib/repositories/campaigns-repo.js';
import { customerSegmentsRepoFor } from '../lib/repositories/customer-segments-repo.js';
import { callRunCampaignNow, type RunCampaignNowOutput } from '../lib/callables/marketing.js';
import { callAiDraftBroadcast, type AiBroadcastDraft } from '../lib/callables/ai.js';
import { RetentionPulseCard } from '../components/retention-pulse-card.js';
import { logger } from '../lib/logger.js';
import { parseRateLimitError, type Campaign, type CustomerSegment } from '@kilat/shared';

/** audienceKey sentinel prefix for "target a saved segment" (vs a prebuilt key). */
const SEGMENT_KEY_PREFIX = 'segment:';

/**
 * /marketing — list + compose + send-now operator surface.
 *
 * MVP scope (Sprint 36): one page with two columns —
 *   - Left: list of existing campaigns sorted by createdAt desc, each
 *     collapsing to summary (title · audience · status pill · stats)
 *     and expanding to show the full content + a "Send now" CTA for
 *     drafts (or "Sent {date}" + stats for completed).
 *   - Right: New-campaign composer (title, body, deep-link, audience,
 *     channel). Channel is fixed to web_push for MVP — schema supports
 *     more, just no UI for them yet.
 *
 * "Send now" calls runCampaignNow which:
 *   - Resolves audience to a list of customerUids (from the tenant
 *     customer-index)
 *   - Per recipient: checks notificationPrefs.marketing.push + quiet hours,
 *     loads FCM tokens, sends via admin SDK
 *   - Writes CampaignDelivery + updates campaign stats + status
 *   - Returns counts for the operator to see immediately
 *
 * Premium-logical / no-dead-ends: every state explicit (loading, empty,
 * load-error, idle-form, validation-error, creating, created-success,
 * sending, sent-summary, send-error). The send-summary names every bucket
 * (sent / skipped-prefs / skipped-quiet / skipped-no-tokens / failed) so
 * the operator never wonders why their "all customers" send only reached
 * 60% — they see the prefs/quiet-hours breakdown.
 *
 * i18n: all user-visible copy resolves via `dashboard.marketing.*` (rule-1
 * debt paid when the saved-segment picker landed). The prebuilt-audience
 * labels/hints stay as `PREBUILT_AUDIENCES` keys resolved through i18n too.
 */

/** Prebuilt (non-saved) audiences. `key` is stable; label/hint come from i18n. */
const PREBUILT_AUDIENCE_KEYS = ['all', 'active_30d', 'silver_tier_min', 'gold_tier_min'] as const;
type PrebuiltKey = (typeof PREBUILT_AUDIENCE_KEYS)[number];

function prebuiltLabel(t: I18n, key: string): string {
  return t.t(`dashboard.marketing.prebuilt.${key}.label`);
}
function prebuiltHint(t: I18n, key: string): string {
  return t.t(`dashboard.marketing.prebuilt.${key}.hint`);
}

export function MarketingPage() {
  const { user, claims } = useAuth();
  const t = useI18n();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;
  const userUid = user?.uid ?? 'unknown';

  const sub = useMemo(() => {
    if (!tenantId) return null;
    const repo = campaignsRepoFor(tenantId);
    return (cb: (vs: Campaign[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const {
    data: campaigns,
    loading,
    error,
  } = useTrackedSnapshot<Campaign[]>(['campaigns', tenantId], sub);

  const segmentsSub = useMemo(() => {
    if (!tenantId) return null;
    const repo = customerSegmentsRepoFor(tenantId);
    return (cb: (vs: CustomerSegment[]) => void) => repo.listSnapshot(cb, () => cb([]));
  }, [tenantId]);
  const { data: segments } = useTrackedSnapshot<CustomerSegment[]>(
    ['marketing-segments', tenantId],
    segmentsSub,
  );

  if (!tenantId) {
    return (
      <Screen
        title={t.t('dashboard.marketing.signInTitle')}
        body={t.t('dashboard.marketing.signInBody')}
        cta={{ label: t.t('dashboard.marketing.backToQueue'), to: '/queue' }}
      />
    );
  }

  const list = (campaigns ?? []).slice().sort((a, b) => tsMs(b.createdAt) - tsMs(a.createdAt));
  const allSegments = segments ?? [];
  const activeSegments = allSegments.filter((s) => s.active);

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.marketing.title')}
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {loading
              ? t.t('common.loading')
              : t.t(
                  list.length === 1
                    ? 'dashboard.marketing.countOne'
                    : 'dashboard.marketing.countOther',
                  { count: list.length },
                )}
          </p>
        </div>
        <div style={linkRowStyle}>
          <Link to="/marketing/share" style={ghostLinkStyle}>
            {t.t('dashboard.nav.shareKit')}
          </Link>
          <Link to="/marketing/journeys" style={ghostLinkStyle}>
            {t.t('dashboard.journeys.manageLink')}
          </Link>
          <Link to="/marketing/segments" style={ghostLinkStyle}>
            {t.t('dashboard.segments.composer.manageLink')}
          </Link>
          <Link to="/queue" style={ghostLinkStyle}>
            {t.t('dashboard.marketing.backToQueue')}
          </Link>
        </div>
      </header>

      <div style={{ marginBottom: 'var(--space-6)' }}>
        <RetentionPulseCard tenantId={tenantId} />
      </div>

      <div className="layout-split-aside">
        <Surface as="section">
          <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {t.t('dashboard.marketing.listHeading')}
          </h2>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t.t('dashboard.marketing.listIntro')}
          </p>
          <div style={{ marginTop: 'var(--space-4)', display: 'grid', gap: 'var(--space-2)' }}>
            {loading ? (
              <InlineNote>{t.t('dashboard.marketing.loading')}</InlineNote>
            ) : error ? (
              <InlineNote tone="error">
                {t.t('dashboard.marketing.loadError', { error: String(error.message ?? error) })}
              </InlineNote>
            ) : list.length === 0 ? (
              <InlineNote>{t.t('dashboard.marketing.empty')}</InlineNote>
            ) : (
              list.map((c) => (
                <CampaignRow key={c.id} tenantId={tenantId} campaign={c} segments={allSegments} />
              ))
            )}
          </div>
        </Surface>

        <Surface as="section">
          <NewCampaignForm tenantId={tenantId} userUid={userUid} segments={activeSegments} />
        </Surface>
      </div>
    </main>
  );
}

// ── row ───────────────────────────────────────────────────────────────

function CampaignRow({
  tenantId,
  campaign,
  segments,
}: {
  readonly tenantId: string;
  readonly campaign: Campaign;
  readonly segments: readonly CustomerSegment[];
}) {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<RunCampaignNowOutput | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const out = await callRunCampaignNow({ tenantId, campaignId: campaign.id });
      setResult(out);
    } catch (err) {
      logger.error('runCampaignNow failed', { campaignId: campaign.id, error: String(err) });
      setError(String((err as Error).message ?? t.t('dashboard.marketing.form.sendFailed')));
    } finally {
      setSending(false);
    }
  }

  const pill = pillForStatus(campaign.status, t);
  const audienceLabel = (() => {
    const aud = campaign.audience;
    if (aud.kind === 'all') return prebuiltLabel(t, 'all');
    if (aud.kind === 'prebuilt') {
      return PREBUILT_AUDIENCE_KEYS.includes(aud.key as PrebuiltKey)
        ? prebuiltLabel(t, aud.key)
        : aud.key;
    }
    const seg = segments.find((s) => s.id === aud.segmentId);
    return seg
      ? t.t('dashboard.segments.composer.audienceLabel', { name: seg.name })
      : t.t('dashboard.segments.composer.audienceUnknown', { id: aud.segmentId });
  })();

  return (
    <article
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3) var(--space-4)',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          padding: 0,
          textAlign: 'left',
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          gap: 'var(--space-3)',
          alignItems: 'baseline',
          cursor: 'pointer',
          font: 'inherit',
          color: 'inherit',
        }}
      >
        <div>
          <p style={{ margin: 0, fontWeight: 500 }}>
            {campaign.content.title['en'] ?? campaign.id}
          </p>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t.t('dashboard.marketing.row.metaCreated', {
              audience: audienceLabel,
              date: formatDate(tsMs(campaign.createdAt)),
            })}
            {campaign.status === 'sent'
              ? t.t('dashboard.marketing.row.sentSuffix', { count: campaign.stats.sent })
              : ''}
          </p>
        </div>
        <span
          style={{
            padding: '4px 10px',
            borderRadius: 'var(--radius-pill)',
            background: pill.bg,
            color: pill.fg,
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
          }}
        >
          {pill.label}
        </span>
        <span
          aria-hidden
          style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}
        >
          {open ? '▴' : '▾'}
        </span>
      </button>

      {open ? (
        <div
          style={{
            marginTop: 'var(--space-3)',
            paddingTop: 'var(--space-3)',
            borderTop: '1px solid var(--color-border)',
            display: 'grid',
            gap: 'var(--space-3)',
          }}
        >
          <div>
            <p style={{ margin: 0, fontWeight: 500 }}>{campaign.content.title['en']}</p>
            <p
              style={{
                margin: 'var(--space-1) 0 0 0',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {campaign.content.body['en']}
            </p>
            {campaign.content.deepLink ? (
              <p
                style={{
                  margin: 'var(--space-1) 0 0 0',
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                }}
              >
                {t.t('dashboard.marketing.row.deepLink', { link: campaign.content.deepLink })}
              </p>
            ) : null}
          </div>

          {campaign.status === 'draft' || campaign.status === 'scheduled' ? (
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => void send()}
                disabled={sending}
                style={{
                  ...primaryButtonStyle,
                  padding: '8px 14px',
                  opacity: sending ? 0.6 : 1,
                  cursor: sending ? 'wait' : 'pointer',
                }}
              >
                {sending
                  ? t.t('dashboard.marketing.row.sending')
                  : t.t('dashboard.marketing.row.sendNow')}
              </button>
            </div>
          ) : null}

          {error ? (
            <p
              role="alert"
              style={{ margin: 0, color: 'var(--color-accent)', fontSize: 'var(--text-meta)' }}
            >
              {error}
            </p>
          ) : null}

          {result ? (
            <div
              role="status"
              style={{
                padding: 'var(--space-3) var(--space-4)',
                borderRadius: 'var(--radius-md)',
                background: 'rgba(13,122,79,0.06)',
                border: '1px solid rgba(13,122,79,0.18)',
                fontSize: 'var(--text-meta)',
              }}
            >
              <p style={{ margin: 0, fontWeight: 500, color: 'var(--color-primary)' }}>
                {t.t('dashboard.marketing.result.sentSummary', {
                  sent: result.sent,
                  total: result.audienceSize,
                })}
              </p>
              <ul
                style={{
                  margin: 'var(--space-2) 0 0 0',
                  padding: '0 0 0 var(--space-5)',
                  color: 'var(--color-on-surface-muted)',
                }}
              >
                {result.skippedNoPrefs > 0 ? (
                  <li>
                    {t.t('dashboard.marketing.result.optedOut', { count: result.skippedNoPrefs })}
                  </li>
                ) : null}
                {result.skippedQuietHours > 0 ? (
                  <li>
                    {t.t('dashboard.marketing.result.quietHours', {
                      count: result.skippedQuietHours,
                    })}
                  </li>
                ) : null}
                {result.skippedNoTokens > 0 ? (
                  <li>
                    {t.t('dashboard.marketing.result.noTokens', { count: result.skippedNoTokens })}
                  </li>
                ) : null}
                {result.failed > 0 ? (
                  <li>{t.t('dashboard.marketing.result.failed', { count: result.failed })}</li>
                ) : null}
              </ul>
            </div>
          ) : null}

          {campaign.status === 'sent' ? (
            <p
              style={{
                margin: 0,
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {t.t('dashboard.marketing.row.sentLine', {
                date: formatDate(tsMs(campaign.updatedAt)),
                count: campaign.stats.sent,
              })}
              {campaign.stats.opened > 0
                ? t.t('dashboard.marketing.row.openedSuffix', { count: campaign.stats.opened })
                : ''}
              {campaign.stats.clicked > 0
                ? t.t('dashboard.marketing.row.clickedSuffix', { count: campaign.stats.clicked })
                : ''}
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

// ── composer ──────────────────────────────────────────────────────────

function NewCampaignForm({
  tenantId,
  userUid,
  segments,
}: {
  readonly tenantId: string;
  readonly userUid: string;
  readonly segments: readonly CustomerSegment[];
}) {
  const t = useI18n();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [deepLink, setDeepLink] = useState('/');
  const [audienceKey, setAudienceKey] = useState<string>('all');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdNotice, setCreatedNotice] = useState<string | null>(null);

  function fillFromDraft(draft: AiBroadcastDraft) {
    setTitle(draft.title);
    setBody(draft.body);
    setDeepLink(draft.deepLink || '/');
    setAudienceKey(draft.audienceKey);
    setCreatedNotice(null);
    setError(null);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setCreatedNotice(null);
    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    if (!trimmedTitle) {
      setError(t.t('dashboard.marketing.form.errTitle'));
      return;
    }
    if (trimmedTitle.length > 65) {
      setError(t.t('dashboard.marketing.form.errTitleLong'));
      return;
    }
    if (!trimmedBody) {
      setError(t.t('dashboard.marketing.form.errBody'));
      return;
    }
    if (trimmedBody.length > 240) {
      setError(t.t('dashboard.marketing.form.errBodyLong'));
      return;
    }
    setBusy(true);
    try {
      const now = Timestamp.now();
      const id = `cmp-${Math.random().toString(36).slice(2, 8)}`;
      const audience = audienceKey.startsWith(SEGMENT_KEY_PREFIX)
        ? { kind: 'segment' as const, segmentId: audienceKey.slice(SEGMENT_KEY_PREFIX.length) }
        : audienceKey === 'all'
          ? { kind: 'all' as const }
          : { kind: 'prebuilt' as const, key: audienceKey };
      await campaignsRepoFor(tenantId).set(id, {
        id,
        channels: ['web_push'],
        content: {
          title: { en: trimmedTitle },
          body: { en: trimmedBody },
          ...(deepLink.trim() ? { deepLink: deepLink.trim() } : {}),
        },
        audience,
        schedule: { sendAt: now, timezone: 'Asia/Kuala_Lumpur' },
        status: 'draft',
        createdBy: userUid,
        createdAt: now,
        updatedAt: now,
        stats: { sent: 0, delivered: 0, opened: 0, clicked: 0, voucherClaimed: 0, booked: 0 },
      });
      setCreatedNotice(t.t('dashboard.marketing.form.createdNotice', { name: trimmedTitle }));
      setTitle('');
      setBody('');
      setDeepLink('/');
      setAudienceKey('all');
    } catch (err) {
      logger.error('campaign create failed', { error: String(err) });
      setError(String((err as Error).message ?? t.t('dashboard.marketing.form.errSave')));
    } finally {
      setBusy(false);
    }
  }

  const segmentSelected = audienceKey.startsWith(SEGMENT_KEY_PREFIX);
  const selectedSegmentId = segmentSelected ? audienceKey.slice(SEGMENT_KEY_PREFIX.length) : '';
  const audienceHint = segmentSelected
    ? t.t('dashboard.segments.composer.hint')
    : prebuiltHint(
        t,
        PREBUILT_AUDIENCE_KEYS.includes(audienceKey as PrebuiltKey) ? audienceKey : 'all',
      );

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <header>
        <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t.t('dashboard.marketing.form.title')}
        </h2>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.marketing.form.subtitle')}
        </p>
      </header>

      <AiDraftPanelInline tenantId={tenantId} onApply={fillFromDraft} />

      {createdNotice ? (
        <p
          role="status"
          style={{
            margin: 0,
            color: 'var(--color-primary)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
          }}
        >
          {createdNotice}
        </p>
      ) : null}

      <label style={fieldStack}>
        <span style={fieldLabel}>{t.t('dashboard.marketing.form.titleLabel')}</span>
        <input
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t.t('dashboard.marketing.placeholders.title')}
          style={inputStyle}
        />
        <span style={hintStyle}>
          {t.t('dashboard.marketing.form.titleHint', { count: title.trim().length })}
        </span>
      </label>

      <label style={fieldStack}>
        <span style={fieldLabel}>{t.t('dashboard.marketing.form.bodyLabel')}</span>
        <textarea
          required
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder={t.t('dashboard.marketing.placeholders.body')}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
        <span style={hintStyle}>
          {t.t('dashboard.marketing.form.bodyHint', { count: body.trim().length })}
        </span>
      </label>

      <label style={fieldStack}>
        <span style={fieldLabel}>{t.t('dashboard.marketing.form.deepLinkLabel')}</span>
        <input
          value={deepLink}
          onChange={(e) => setDeepLink(e.target.value)}
          placeholder={t.t('dashboard.marketing.placeholders.deepLink')}
          style={inputStyle}
        />
        <span style={hintStyle}>{t.t('dashboard.marketing.form.deepLinkHint')}</span>
      </label>

      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={fieldLabel}>{t.t('dashboard.marketing.form.audienceLegend')}</legend>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
            marginTop: 'var(--space-2)',
          }}
        >
          {PREBUILT_AUDIENCE_KEYS.map((key) => {
            const selected = audienceKey === key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={selected}
                onClick={() => setAudienceKey(key)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 'var(--radius-pill)',
                  border: selected
                    ? '1px solid var(--color-primary)'
                    : '1px solid var(--color-border-strong)',
                  background: selected ? 'var(--color-primary)' : 'transparent',
                  color: selected ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
                  fontSize: 'var(--text-meta)',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {prebuiltLabel(t, key)}
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
          <span style={fieldLabel}>{t.t('dashboard.segments.composer.savedLegend')}</span>
          {segments.length === 0 ? (
            <p style={{ margin: 0, ...hintStyle }}>
              {t.t('dashboard.segments.composer.noSegments')}{' '}
              <Link to="/marketing/segments" style={{ color: 'var(--color-primary)' }}>
                {t.t('dashboard.segments.composer.manageLink')}
              </Link>
            </p>
          ) : (
            <select
              value={selectedSegmentId}
              onChange={(e) =>
                setAudienceKey(e.target.value ? `${SEGMENT_KEY_PREFIX}${e.target.value}` : 'all')
              }
              style={inputStyle}
            >
              <option value="">{t.t('dashboard.segments.composer.savedPlaceholder')}</option>
              {segments.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <p
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {audienceHint}
        </p>
      </fieldset>

      {error ? (
        <p
          role="alert"
          style={{ margin: 0, color: 'var(--color-accent)', fontSize: 'var(--text-meta)' }}
        >
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={busy}
        style={{
          ...primaryButtonStyle,
          opacity: busy ? 0.6 : 1,
          cursor: busy ? 'wait' : 'pointer',
        }}
      >
        {busy ? t.t('dashboard.marketing.form.saving') : t.t('dashboard.marketing.form.saveDraft')}
      </button>

      <p
        style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}
      >
        {t.t('dashboard.marketing.form.draftFooter')}
      </p>
    </form>
  );
}

// ── helpers + styles ──────────────────────────────────────────────────

function pillForStatus(s: Campaign['status'], t: I18n): { bg: string; fg: string; label: string } {
  switch (s) {
    case 'draft':
      return {
        bg: 'var(--color-border)',
        fg: 'var(--color-on-surface-muted)',
        label: t.t('dashboard.marketing.status.draft'),
      };
    case 'scheduled':
      return {
        bg: 'rgba(245,158,11,0.10)',
        fg: '#b45309',
        label: t.t('dashboard.marketing.status.scheduled'),
      };
    case 'sending':
      return {
        bg: 'rgba(245,158,11,0.10)',
        fg: '#b45309',
        label: t.t('dashboard.marketing.status.sending'),
      };
    case 'sent':
      return {
        bg: 'rgba(13,122,79,0.10)',
        fg: 'var(--color-primary)',
        label: t.t('dashboard.marketing.status.sent'),
      };
    case 'failed':
      return {
        bg: 'rgba(239,68,68,0.10)',
        fg: 'var(--color-accent)',
        label: t.t('dashboard.marketing.status.failed'),
      };
  }
}

function tsMs(ts: { toMillis?: () => number; seconds: number } | undefined): number {
  if (!ts) return 0;
  return typeof ts.toMillis === 'function' ? ts.toMillis() : ts.seconds * 1000;
}

function formatDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString();
}

function InlineNote({
  children,
  tone = 'muted',
}: {
  readonly children: React.ReactNode;
  readonly tone?: 'muted' | 'error';
}) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        margin: 0,
        padding: 'var(--space-3) var(--space-4)',
        fontSize: 'var(--text-meta)',
        color: tone === 'error' ? 'var(--color-accent)' : 'var(--color-on-surface-muted)',
        background: tone === 'error' ? 'rgba(239,68,68,0.06)' : 'var(--tint-subtle)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {children}
    </p>
  );
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <div
        style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'center', maxWidth: 380 }}
      >
        <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {body}
        </p>
        {cta ? (
          <Link
            to={cta.to}
            style={{ ...primaryButtonStyle, textDecoration: 'none', display: 'inline-flex' }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

const fieldStack: React.CSSProperties = { display: 'grid', gap: 'var(--space-2)' };

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const hintStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

// ── AI broadcast draft panel ───────────────────────────────────────────
//
// Sprint 78. Sits at the top of NewCampaignForm; operator types intent
// → AI returns a structured draft → operator either "Apply" (fills the
// existing form fields below) or "Try again". Mirror of the
// AiDraftPanel pattern from /loyalty/promotions (Sprint 75) — same
// states, same NoOp + parse-failed + rate-limit handling.

function AiDraftPanelInline({
  tenantId,
  onApply,
}: {
  readonly tenantId: string;
  readonly onApply: (draft: AiBroadcastDraft) => void;
}) {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const [intent, setIntent] = useState('');
  const [stage, setStage] = useState<
    | { kind: 'idle' }
    | { kind: 'drafting' }
    | { kind: 'suggested'; draft: AiBroadcastDraft; cacheHit: boolean }
    | { kind: 'parse_failed'; rawText: string }
    | { kind: 'noop' }
    | { kind: 'failed'; message: string; retryAfterSec?: number }
  >({ kind: 'idle' });

  async function draftIt() {
    const text = intent.trim();
    if (text.length < 4) return;
    setStage({ kind: 'drafting' });
    try {
      const out = await callAiDraftBroadcast({ intent: text, tenantId });
      if (out.provider === 'noop') {
        setStage({ kind: 'noop' });
        return;
      }
      if (!out.draft) {
        setStage({ kind: 'parse_failed', rawText: out.rawText });
        return;
      }
      setStage({ kind: 'suggested', draft: out.draft, cacheHit: out.cacheHit });
    } catch (err) {
      const rl = parseRateLimitError(err);
      if (rl) {
        setStage({ kind: 'failed', message: rl.message, retryAfterSec: rl.retryAfterSec });
        return;
      }
      setStage({
        kind: 'failed',
        message: (err as Error).message ?? t.t('dashboard.marketing.ai.cantReach'),
      });
    }
  }

  function apply(draft: AiBroadcastDraft) {
    onApply(draft);
    setStage({ kind: 'idle' });
    setIntent('');
    setOpen(false);
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 14px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid rgba(13,122,79,0.22)',
          background: 'rgba(13,122,79,0.04)',
          color: 'var(--color-primary)',
          fontWeight: 600,
          fontSize: 'var(--text-meta)',
          cursor: 'pointer',
          fontFamily: 'inherit',
          width: 'fit-content',
        }}
      >
        <span aria-hidden style={{ fontSize: 13, lineHeight: 1 }}>
          {'✦'}
        </span>
        {t.t('dashboard.marketing.ai.openButton')}
      </button>
    );
  }

  return (
    <div
      style={{
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid rgba(13,122,79,0.18)',
        background: 'rgba(13,122,79,0.04)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          aria-hidden
          style={{
            display: 'inline-grid',
            placeItems: 'center',
            width: 26,
            height: 26,
            borderRadius: 8,
            background: 'linear-gradient(135deg, #0d7a4f 0%, #0f8659 100%)',
            color: '#ffffff',
            fontSize: 12,
          }}
        >
          {'✦'}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-meta)' }}>
            {t.t('dashboard.marketing.ai.panelTitle')}
          </p>
          <p style={{ margin: '2px 0 0 0', fontSize: 11, color: 'var(--color-on-surface-muted)' }}>
            {t.t('dashboard.marketing.ai.panelSubtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setStage({ kind: 'idle' });
          }}
          aria-label={t.t('dashboard.marketing.ai.close')}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--color-on-surface-muted)',
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
            padding: 4,
          }}
        >
          {'×'}
        </button>
      </header>

      <textarea
        value={intent}
        onChange={(e) => setIntent(e.target.value)}
        placeholder={t.t('dashboard.marketing.ai.intentPlaceholder')}
        rows={2}
        maxLength={400}
        disabled={stage.kind === 'drafting'}
        style={{
          padding: '10px 12px',
          border: '1px solid var(--color-border-strong)',
          borderRadius: 10,
          fontSize: 14,
          fontFamily: 'inherit',
          resize: 'vertical',
          background: 'var(--color-surface)',
          color: 'var(--color-on-surface)',
        }}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {EXAMPLE_BROADCAST_INTENT_KEYS.map((key) => {
          const label = t.t(`dashboard.marketing.ai.examples.${key}`);
          return (
            <button
              key={key}
              type="button"
              disabled={stage.kind === 'drafting'}
              onClick={() => setIntent(label)}
              style={exampleChipStyle}
            >
              {label}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => void draftIt()}
        disabled={intent.trim().length < 4 || stage.kind === 'drafting'}
        style={{
          ...primaryButtonStyle,
          width: 'fit-content',
          opacity: intent.trim().length < 4 || stage.kind === 'drafting' ? 0.6 : 1,
        }}
      >
        {stage.kind === 'drafting'
          ? t.t('dashboard.marketing.ai.drafting')
          : t.t('dashboard.marketing.ai.suggest')}
      </button>

      {stage.kind === 'suggested' ? (
        <BroadcastDraftPreview
          draft={stage.draft}
          cacheHit={stage.cacheHit}
          onApply={() => apply(stage.draft)}
        />
      ) : null}
      {stage.kind === 'parse_failed' ? (
        <p
          style={{
            margin: 0,
            padding: '10px 14px',
            borderRadius: 8,
            background: 'rgba(245,158,11,0.06)',
            border: '1px solid rgba(245,158,11,0.22)',
            color: '#b45309',
            fontSize: 'var(--text-meta)',
            lineHeight: 1.5,
          }}
        >
          {t.t('dashboard.marketing.ai.parseFailed')}
        </p>
      ) : null}
      {stage.kind === 'noop' ? (
        <p
          style={{
            margin: 0,
            padding: '10px 14px',
            borderRadius: 8,
            background: 'rgba(245,158,11,0.06)',
            border: '1px solid rgba(245,158,11,0.22)',
            color: '#b45309',
            fontSize: 'var(--text-meta)',
            lineHeight: 1.5,
          }}
        >
          <strong>{t.t('dashboard.marketing.ai.noopTitle')}</strong>{' '}
          {t.t('dashboard.marketing.ai.noopBody')}
        </p>
      ) : null}
      {stage.kind === 'failed' ? (
        <p
          role="alert"
          style={{
            margin: 0,
            padding: '10px 14px',
            borderRadius: 8,
            background: 'rgba(239,68,68,0.06)',
            border: '1px solid rgba(239,68,68,0.20)',
            color: '#991b1b',
            fontSize: 'var(--text-meta)',
            lineHeight: 1.5,
          }}
        >
          {stage.message}
          {stage.retryAfterSec ? (
            <span
              style={{ display: 'block', marginTop: 4, color: 'var(--color-on-surface-muted)' }}
            >
              {t.t('dashboard.marketing.ai.retryIn', { count: stage.retryAfterSec })}
            </span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

/** Stable keys for the example intent chips; labels resolve via i18n. */
const EXAMPLE_BROADCAST_INTENT_KEYS = ['thankGold', 'weekend', 'hariRaya', 'winBack'] as const;

function BroadcastDraftPreview({
  draft,
  cacheHit,
  onApply,
}: {
  readonly draft: AiBroadcastDraft;
  readonly cacheHit: boolean;
  readonly onApply: () => void;
}) {
  const t = useI18n();
  const audienceLabel = PREBUILT_AUDIENCE_KEYS.includes(draft.audienceKey as PrebuiltKey)
    ? prebuiltLabel(t, draft.audienceKey)
    : draft.audienceKey;
  return (
    <div
      style={{
        background: 'var(--color-surface)',
        border: '1px solid rgba(13,122,79,0.22)',
        borderRadius: 10,
        padding: 'var(--space-3) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-meta)' }}>
          {t.t('dashboard.marketing.ai.suggestedDraft')}
        </p>
        {cacheHit ? (
          <span
            style={{
              fontSize: 9,
              padding: '2px 6px',
              borderRadius: 999,
              background: 'rgba(13,122,79,0.10)',
              color: 'var(--color-primary)',
              fontWeight: 600,
              letterSpacing: 0.3,
            }}
            title={t.t('dashboard.marketing.ai.cachedTitle')}
          >
            {t.t('dashboard.marketing.ai.cached')}
          </span>
        ) : null}
      </div>
      {/* Phone-frame-style preview */}
      <div
        style={{
          background: 'var(--tint-subtle)',
          border: '1px solid var(--color-border)',
          borderRadius: 12,
          padding: 'var(--space-3)',
        }}
      >
        <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-meta)', lineHeight: 1.3 }}>
          {draft.title}
        </p>
        <p
          style={{
            margin: '4px 0 0 0',
            fontSize: 12,
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {draft.body}
        </p>
      </div>
      <dl
        style={{
          margin: 0,
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          rowGap: 4,
          columnGap: 12,
          fontSize: 11,
        }}
      >
        <dt style={previewLabel}>{t.t('dashboard.marketing.ai.previewAudience')}</dt>
        <dd style={previewValue}>{audienceLabel}</dd>
        <dt style={previewLabel}>{t.t('dashboard.marketing.ai.previewOpens')}</dt>
        <dd style={previewValue}>
          <code style={{ fontFamily: 'inherit' }}>{draft.deepLink}</code>
        </dd>
        <dt style={previewLabel}>{t.t('dashboard.marketing.ai.previewWhy')}</dt>
        <dd style={previewValue}>{draft.rationale}</dd>
      </dl>
      <button
        type="button"
        onClick={onApply}
        style={{ ...primaryButtonStyle, marginTop: 4, width: 'fit-content' }}
      >
        {t.t('dashboard.marketing.ai.applyToForm')}
      </button>
      <p
        style={{
          margin: 0,
          fontSize: 11,
          color: 'var(--color-on-surface-muted)',
          fontStyle: 'italic',
        }}
      >
        {t.t('dashboard.marketing.ai.disclaimer')}
      </p>
    </div>
  );
}

const exampleChipStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid rgba(13,122,79,0.16)',
  color: 'var(--color-primary)',
  padding: '6px 12px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const previewLabel: React.CSSProperties = {
  color: 'var(--color-on-surface-muted)',
  fontWeight: 600,
  fontSize: 10,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  paddingTop: 2,
};

const previewValue: React.CSSProperties = {
  margin: 0,
  color: 'var(--color-on-surface)',
  fontSize: 12,
  lineHeight: 1.4,
};
